// src/pages/FillInspection.js
import React, { useEffect, useState, useRef } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { api } from "../lib/api";
import LocationField from "../components/inputs/LocationField.jsx";
import FormQuestionRenderer from "../components/FormQuestionRenderer.jsx";

export default function FillInspection() {
  const { templateId } = useParams();
  const navigate = useNavigate();
  const [template, setTemplate] = useState(null);
  const [answers, setAnswers] = useState({});
  const [saving, setSaving] = useState(false);
  const [documentNumber, setDocumentNumber] = useState(null);
  const [showCancelConfirm, setShowCancelConfirm] = useState(false);
  const location = useLocation();
  const qs = useRef(new URLSearchParams(location.search)).current;
  const [currentInspectionId, setCurrentInspectionId] = useState(qs.get("inspectionId") || null);

  // Projects for "Site" selector
  const [projects, setProjects] = useState([]);
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const list = await api.get("/projects");
        const filtered = Array.isArray(list)
          ? list.filter(p => !p.archived && p.active !== false && p.active !== 0)
          : [];
        if (!cancelled) setProjects(filtered);
      } catch {
        // non-fatal
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // ---- read-only & print flags from URL ----
  const READ_ONLY = qs.get("readonly") === "1";
  const AUTO_PRINT = qs.get("print") === "1";

  useEffect(() => {
    if (AUTO_PRINT) {
      // give the page time to render, then open print dialog
      const t = setTimeout(() => window.print(), 400);
      return () => clearTimeout(t);
    }
  }, [AUTO_PRINT]);

  useEffect(() => {
    let cancelled = false;

    async function loadTemplate() {
      try {
        const data = await api.get(`/templates/${templateId}`);
        if (cancelled) return;

        setTemplate(data);

        // Prepopulate answer structure for ALL pages & sections
        const ans = {};
        const u = JSON.parse(localStorage.getItem("user") || "null");
        const currentUserName =
          u?.name || u?.displayName || (u?.email ? u.email.split("@")[0] : "");
        (data?.pages || []).forEach((page) => {
          (page?.sections || []).forEach((section) => {
            (section?.questions || []).forEach((q) => {
              if (!(q.id in ans)) {
                ans[q.id] = q.type === "person" && currentUserName ? currentUserName : "";
              }
            });
          });
        });

        setAnswers(ans);
      } catch (err) {
        console.error(err);
        alert("Failed to load template.");
      }
    }

    loadTemplate();
    return () => { cancelled = true; };
  }, [templateId]);

  // Prefill answers when editing an existing inspection (merge missing 'person')
  useEffect(() => {
    if (!currentInspectionId) return;
    (async () => {
      try {
        const data = await api.get(`/inspections/${currentInspectionId}`);
        setDocumentNumber(data?.documentNumber ?? null);
        const loaded = data?.answers || {};

        // if template + user available, inject user name where a person field is empty
        const u = JSON.parse(localStorage.getItem("user") || "null");
        const currentUserName =
          u?.name || u?.displayName || (u?.email ? u.email.split("@")[0] : "");

        if (template && currentUserName) {
          const merged = { ...loaded };
          (template.pages || []).forEach((page) =>
            (page.sections || []).forEach((sec) =>
              (sec.questions || []).forEach((q) => {
                if (q.type === "person") {
                  const k = q.id;
                  if (merged[k] == null || merged[k] === "") {
                    merged[k] = currentUserName;
                  }
                }
              })
            )
          );
          setAnswers(merged);
        } else {
          setAnswers(loaded);
        }
      } catch {
        // non-fatal
      }
    })();
  }, [currentInspectionId, template]);

  // (removed) auto-create on first load — creation now happens only on Save/Complete
  // to avoid consuming document numbers when the user cancels.

  function handleChange(qid, val) {
    setAnswers(a => ({ ...a, [qid]: val }));
  }

  // Remove DOM nodes, React synthetic events, functions, and circular refs
  function sanitize(value) {
    const seen = new WeakSet();
    const walk = (v) => {
      if (v == null) return v;
      const t = typeof v;
      if (t === "string" || t === "number" || t === "boolean") return v;
      if (t === "function") return undefined;
      if (v instanceof Date) return v.toISOString();
      // DOM / React event-ish checks
      if (v.nativeEvent || v.target || v._reactName || v._reactFiber || v.stateNode) return undefined;
      if (typeof Element !== "undefined" && v instanceof Element) return undefined;
      if (Array.isArray(v)) return v.map(walk);
      if (t === "object") {
        if (seen.has(v)) return undefined;
        seen.add(v);
        const out = {};
        for (const k in v) out[k] = walk(v[k]);
        return out;
      }
      return undefined;
    };
    return walk(value);
  }

  // --- helpers to detect meaningful user changes (ignore system-filled fields) ---
  function getQuestionTypeById(qid) {
    for (const p of (template?.pages || [])) {
      for (const s of (p.sections || [])) {
        for (const q of (s.questions || [])) {
          if (String(q.id) === String(qid)) return (q.type || "").toLowerCase();
        }
      }
    }
    return "";
  }
  function isMeaningfulValue(qid, v) {
    const t = getQuestionTypeById(qid);
    if (t === "document-number" || t === "person") return false; // ignore system fields
    if (v == null) return false;
    if (typeof v === "string") return v.trim().length > 0;
    if (Array.isArray(v)) return v.length > 0;
    if (typeof v === "object") return Object.keys(v).length > 0;
    return !!v;
  }
  function hasMeaningfulAnswers(obj) {
    return Object.entries(obj || {}).some(([k, v]) => isMeaningfulValue(k, v));
  }

  // Cancel, Complete, Save handlers
  async function handleCancel() {
    if (!currentInspectionId) {
      navigate("/inspections");
      return;
    }
    const hasAnyNow = hasMeaningfulAnswers(answers);
    if (!hasAnyNow) {
      try {
        if (typeof api.delete === "function") await api.delete(`/inspections/${currentInspectionId}`);
        else if (typeof api.del === "function") await api.del(`/inspections/${currentInspectionId}`);
        else {
          const base = process.env.REACT_APP_API_URL || "";
          await fetch(`${base}/inspections/${encodeURIComponent(currentInspectionId)}`, { method: "DELETE" });
        }
      } catch (e) {
        console.error("Cancel-delete failed", e);
      }
      navigate("/inspections");
      return;
    }
    // Existing inspection with edits → just leave (do NOT save). All unsaved changes are discarded.
    navigate("/inspections");
  }

  async function handleComplete() {
    await handleSave("complete");
  }

  async function handleSave(nextStatus = "incomplete") {
    // If a click event slipped in, default to "incomplete"
    if (nextStatus && typeof nextStatus === "object") nextStatus = "incomplete";
    setSaving(true);
    try {
      // Strip out any 'document-number' answer keys (server owns the number)
      const docNumberIds = new Set();
      (template?.pages || []).forEach(p =>
        (p.sections || []).forEach(s =>
          (s.questions || []).forEach(q => {
            if ((q.type || "").toLowerCase() === "document-number") docNumberIds.add(q.id);
          })
        )
      );
      const filteredAnswers = Object.fromEntries(
        Object.entries(sanitize(answers) || {}).filter(([k]) => !docNumberIds.has(k))
      );

      const templateTitle =
        template?.pages?.[0]?.title || template?.title || "Untitled";

      const payload = {
        templateId,
        templateName: templateTitle,
        answers: filteredAnswers,
        status: nextStatus,
        meta: {
          templateId,
          templateName: templateTitle,
        },
      };

      if (currentInspectionId) {
        await api.patch(`/inspections/${currentInspectionId}`, payload);
        navigate("/inspections");
      } else {
        const created = await api.post("/inspections", payload);
        setDocumentNumber(created?.documentNumber ?? null);
        if (created?.id) {
          setCurrentInspectionId(created.id);
          try {
            const url = `/inspections/fill/${templateId}?inspectionId=${created.id}${qs.get("readonly")==="1" ? "&readonly=1" : ""}${qs.get("print")==="1" ? "&print=1" : ""}`;
            window.history.replaceState(null, "", url);
          } catch {}
        }
        // After first create, always go back to list (one-click save)
        navigate("/inspections");
        return;
      }
    } catch (err) {
      alert(err.message);
    } finally {
      setSaving(false);
    }
  }

  if (!template) return <div style={{ padding: 40 }}>Loading...</div>;

  return (
    <div
      className="inspection-page"
      style={{
        maxWidth: "min(840px, 100%)", // responsive: up to 840px, otherwise 100%
        margin: "24px auto",
        background: "#fff",
        borderRadius: 12,
        boxShadow: "0 4px 32px #0002",
        padding: 24,
      }}
    >
      <div
        className="inspection-page-header"
        style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 24 }}
      >
        <div style={{ display: "flex", gap: 18, alignItems: "center" }}>
          {template.logo && (
            <img
              src={template.logo}
              alt="logo"
              style={{ width: 54, height: 54, borderRadius: 12, objectFit: "contain" }}
            />
          )}
          <div>
            <div style={{ fontWeight: 700, fontSize: 22 }}>{template.title}</div>
            <div style={{ color: "#888", fontSize: 16 }}>{template.description}</div>
          </div>
        </div>

        {documentNumber && (
          <div
            style={{
              background: "#e3f2fd",
              color: "#1976d2",
              fontWeight: 600,
              fontSize: 14,
              padding: "6px 14px",
              borderRadius: 999,
              boxShadow: "0 1px 3px #0001",
              whiteSpace: "nowrap"
            }}
          >
            Document #{documentNumber}
          </div>
        )}
      </div>
      {template.pages.map((page, pageIdx) => (
        <div key={page.id} style={{ marginBottom: 40 }}>
          <div style={{ fontWeight: 600, fontSize: 14, color: "#6b7280", marginBottom: 8 }}>
            Page {pageIdx + 1} of {template.pages.length}{page.title ? ` — ${page.title}` : ""}
          </div>
          {page.sections.map((section, sIdx) => (
            <div
              key={section.id}
              className="inspection-section-block"
              style={{
                marginBottom: 34,
                paddingTop: sIdx === 0 ? 0 : 16,
                borderTop: sIdx === 0 ? "none" : "1px solid #e5e7eb"
              }}
            >
              <div style={{ fontWeight: 700, fontSize: 16, marginBottom: 8 }}>
                {(section.title && section.title.trim()) ? section.title : `Section ${sIdx + 1}`}
              </div>
              {section.description && section.description.trim()
                ? <div style={{ color: "#6b7280", fontSize: 13, marginBottom: 10 }}>{section.description}</div>
                : null}
              {(section.questions || []).map(q => {
                const rawType = (q?.type ?? "").toString().toLowerCase().trim();
                const labelSource = q?.label ?? q?.text ?? q?.title ?? "";
                const labelHint = labelSource.toString().toLowerCase();
                const isCheckbox = rawType === "checkbox";
                const isInstruction = rawType === "instruction";
                const isSectionRow = rawType === "section";
                // Super-permissive label matcher (punctuation, colons, etc.)
                const locationLabelRegex =
                  /(location|site\s*location|job\s*site|address|coords?|coordinates|lat|lng|gps|map|pin|where)/;

                // Section rows: structural grouping only (no input)
                if (isSectionRow) {
                  return (
                    <div
                      key={q.id}
                      style={{
                        marginTop: 20,
                        marginBottom: 8,
                        fontWeight: 600,
                        fontSize: 15,
                        color: "#1d4ed8",
                      }}
                    >
                      {labelSource || "Section"}
                    </div>
                  );
                }

                // NEW: scan the whole question object for location-ish hints
                const blob = JSON.stringify(q || {}).toLowerCase();
                const blobHasLocationHints =
                  blob.includes('"location"') ||
                  blob.includes('"gps"') ||
                  blob.includes('"lat"') ||
                  blob.includes('"lng"') ||
                  blob.includes('"latitude"') ||
                  blob.includes('"longitude"') ||
                  blob.includes('"coordinate"') ||
                  blob.includes('"address"') ||
                  blob.includes('select on map');

                // Final “brick wall” catch: if *either* type alias OR any hint in label/object → treat as location
                const treatAsLocation =
                  ["location", "geo", "geolocation", "map", "gps", "address"].includes(rawType) ||
                  locationLabelRegex.test(labelHint) ||
                  labelHint.includes("location") ||       // <- permissive includes
                  labelHint.includes("address")  ||       // <- permissive includes
                  blobHasLocationHints;

                if (process.env.NODE_ENV !== "production") {
                  console.log("Row debug:", {
                    id: q?.id,
                    rawType,
                    label: labelSource,
                    treatAsLocation
                  });
                }
                return (
                  <div
                    key={q.id}
                    style={{
                      marginBottom: 18,
                      padding: 12,
                      borderRadius: 12,
                      border: "1px solid #e5e7eb",
                      background: "#f9fafb",
                    }}
                  >
                    {!isCheckbox && !isInstruction && (
                      <>
                        <label style={{ fontWeight: 500 }}>
                          {(q.label ?? q.text ?? q.title) || "Location"}
                          {q.required && <span style={{ color: "#d32f2f" }}> *</span>}
                        </label>
                        <br />
                      </>
                    )}
                    {treatAsLocation ? (
                      <LocationAnswer
                        value={answers[q.id]}
                        onChange={(v) => handleChange(q.id, v)}
                        label={q.label ?? q.text ?? q.title}
                        readOnly={READ_ONLY}
                      />
                    ) : (
                      <QuestionInput
                        q={q}
                        val={answers[q.id]}
                        setVal={(v) => handleChange(q.id, v)}
                        readOnly={READ_ONLY}
                        projects={projects}
                        documentNumber={documentNumber}
                        template={template}
                      />
                    )}
                  </div>
                );
              })}
            </div>
          ))}
        </div>
      ))}
      
      {READ_ONLY && (
        <div className="inspection-print-footer">
          <div>Generated by Moravia Safety App</div>
          <div>Document #{documentNumber ?? "—"}</div>
        </div>
      )}

      {!READ_ONLY && (
        <div style={{ display: "flex", gap: 12, marginTop: 16 }}>
          <button
            type="button"
            disabled={saving}
            style={{
              background: "#1976d2",
              color: "#fff",
              border: "none",
              borderRadius: 8,
              padding: "12px 30px",
              fontWeight: 700,
              fontSize: 18,
              cursor: saving ? "not-allowed" : "pointer"
            }}
            onClick={() => handleSave()}
          >
            {saving ? "Saving..." : "Save Inspection"}
          </button>

          <button
            type="button"
            disabled={saving}
            style={{
              background: "#16a34a",
              color: "#fff",
              border: "none",
              borderRadius: 8,
              padding: "12px 30px",
              fontWeight: 700,
              fontSize: 18,
              cursor: saving ? "not-allowed" : "pointer"
            }}
            onClick={handleComplete}
          >
            Complete Inspection
          </button>

          <button
            type="button"
            disabled={saving}
            style={{
              background: "#e5e7eb",
              color: "#111827",
              border: "none",
              borderRadius: 8,
              padding: "12px 30px",
              fontWeight: 700,
              fontSize: 18,
              cursor: saving ? "not-allowed" : "pointer"
            }}
            onClick={() => setShowCancelConfirm(true)}
          >
            Cancel
          </button>
        </div>
      )}
      
      {/* Cancel confirmation modal */}
      {showCancelConfirm && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl w-full max-w-md p-6 shadow-xl">
            <h3 className="text-lg font-semibold mb-2">Discard changes?</h3>
            <p className="text-sm text-gray-600 mb-4">
              All unsaved changes will be lost. Do you want to continue?
            </p>
            <div className="flex justify-end gap-2">
              <button
                className="px-4 py-2 rounded-md bg-gray-200 text-gray-800"
                onClick={() => setShowCancelConfirm(false)}
              >
                Cancel
              </button>
              <button
                className="px-4 py-2 rounded-md bg-red-600 text-white"
                onClick={() => {
                  setShowCancelConfirm(false);
                  handleCancel();
                }}
              >
                OK
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function SignatureAnswer({ value, onChange, readOnly }) {
  const [isPadOpen, setIsPadOpen] = useState(false);
  const [hasDrawn, setHasDrawn] = useState(false);
  const canvasRef = useRef(null);
  const drawingRef = useRef(false);

  const getSafeValue = () => {
    if (value && typeof value === "object") {
      return {
        fullName: "",
        imageDataUrl: null,
        signedAt: null,
        ...value,
      };
    }
    return {
      fullName: typeof value === "string" ? value : "",
      imageDataUrl: null,
      signedAt: null,
    };
  };

  const safeValue = getSafeValue();

  const handleNameChange = (e) => {
    const base = getSafeValue();
    onChange({ ...base, fullName: e.target.value });
  };

  const initCanvas = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const rect = canvas.getBoundingClientRect();
    const dpr = window.devicePixelRatio || 1;
    canvas.width = rect.width * dpr;
    canvas.height = rect.height * dpr;
    const ctx = canvas.getContext("2d");
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);

    ctx.fillStyle = "#e5e7eb";
    ctx.fillRect(0, 0, rect.width, rect.height);

    ctx.strokeStyle = "#9ca3af";
    ctx.lineWidth = 1.5;
    ctx.setLineDash([5, 4]);
    const midY = rect.height * 0.65;
    ctx.beginPath();
    ctx.moveTo(16, midY);
    ctx.lineTo(rect.width - 16, midY);
    ctx.stroke();
    ctx.setLineDash([]);
  };

  const openPad = () => {
    if (readOnly) return;
    setIsPadOpen(true);
    setHasDrawn(false);
    setTimeout(initCanvas, 0);
  };

  const handleClear = () => {
    initCanvas();
    setHasDrawn(false);
  };

  const getPos = (e) => {
    const canvas = canvasRef.current;
    if (!canvas) return null;
    const rect = canvas.getBoundingClientRect();
    const point = e.touches ? e.touches[0] : e;
    return {
      canvas,
      x: point.clientX - rect.left,
      y: point.clientY - rect.top,
    };
  };

  const handlePointerDown = (e) => {
    if (readOnly) return;
    e.preventDefault();
    const pos = getPos(e);
    if (!pos) return;
    const ctx = pos.canvas.getContext("2d");
    drawingRef.current = true;
    ctx.strokeStyle = "#111827";
    ctx.lineWidth = 2.2;
    ctx.lineCap = "round";
    ctx.beginPath();
    ctx.moveTo(pos.x, pos.y);
  };

  const handlePointerMove = (e) => {
    if (!drawingRef.current || readOnly) return;
    e.preventDefault();
    const pos = getPos(e);
    if (!pos) return;
    const ctx = pos.canvas.getContext("2d");
    ctx.lineTo(pos.x, pos.y);
    ctx.stroke();
    setHasDrawn(true);
  };

  const handlePointerUp = (e) => {
    if (!drawingRef.current) return;
    e.preventDefault();
    drawingRef.current = false;
  };

  const handleDone = () => {
    const canvas = canvasRef.current;
    if (!canvas) {
      setIsPadOpen(false);
      return;
    }
    const base = getSafeValue();
    let imageDataUrl = base.imageDataUrl || null;

    if (hasDrawn) {
      try {
        imageDataUrl = canvas.toDataURL("image/png");
      } catch {
        // ignore in case of preview errors
      }
    }

    const signedAt = hasDrawn ? Date.now() : base.signedAt || null;
    onChange({ ...base, imageDataUrl, signedAt });
    setIsPadOpen(false);
    setHasDrawn(false);
  };

  const signedAtLabel =
    safeValue.signedAt != null
      ? new Date(safeValue.signedAt).toLocaleString()
      : null;

  return (
    <div style={{ marginTop: 4 }}>
      {/* main row: technician name + sign button */}
      <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
        <input
          type="text"
          placeholder="Enter name of signee"
          value={safeValue.fullName || ""}
          onChange={handleNameChange}
          readOnly={readOnly}
          disabled={readOnly}
          style={{
            flex: 1,
            padding: "10px 12px",
            borderRadius: 10,
            border: "1px solid #d1d5db",
            fontSize: 15,
          }}
        />
        <button
          type="button"
          onClick={openPad}
          disabled={readOnly}
          style={{
            width: 40,
            height: 40,
            borderRadius: 10,
            border: "none",
            background: "#6366f1",
            color: "#ffffff",
            fontSize: 18,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            boxShadow: "0 6px 10px rgba(99,102,241,0.35)",
            cursor: readOnly ? "not-allowed" : "pointer",
          }}
          title={safeValue.imageDataUrl ? "View / re-sign" : "Sign"}
        >
          ✍️
        </button>
      </div>

      {/* inline signature box (only when we have a signature) */}
      {safeValue.imageDataUrl && (
        <div
          style={{
            marginTop: 10,
            borderRadius: 12,
            border: "1px dashed #d1d5db",
            padding: 10,
            background: "#f3f4f6",
          }}
        >
          <div
            style={{
              height: 70,
              borderRadius: 10,
              background: "#f9fafb",
              position: "relative",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              overflow: "hidden",
            }}
          >
            <div
              style={{
                position: "absolute",
                left: 20,
                right: 20,
                bottom: 18,
                borderBottom: "1px dashed #d1d5db",
              }}
            />
            <img
              src={safeValue.imageDataUrl}
              alt="Signature"
              style={{
                maxWidth: "100%",
                maxHeight: "100%",
                objectFit: "contain",
                zIndex: 1,
              }}
            />
          </div>
          {signedAtLabel && (
            <div
              style={{
                marginTop: 4,
                fontSize: 11,
                color: "#6b7280",
              }}
            >
              Signed: {signedAtLabel}
            </div>
          )}
        </div>
      )}

      {/* SMALL CENTERED signing dialog (no overlay in read-only) */}
      {isPadOpen && !readOnly && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            zIndex: 50,
            background: "rgba(0,0,0,0.45)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <div
            style={{
              width: "min(480px, 90%)",
              height: 260,
              borderRadius: 20,
              background: "#f9fafb",
              boxShadow: "0 18px 40px rgba(15,23,42,0.35)",
              display: "flex",
              flexDirection: "column",
            }}
          >
            {/* top bar with time + Clear / Done */}
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                padding: "10px 16px",
                borderBottom: "1px solid #e5e7eb",
              }}
            >
              <button
                type="button"
                onClick={handleClear}
                style={{
                  border: "none",
                  background: "transparent",
                  color: "#2563eb",
                  fontSize: 14,
                }}
              >
                Clear
              </button>
              <div style={{ fontSize: 14, fontWeight: 600 }}>
                {new Date().toLocaleTimeString([], {
                  hour: "2-digit",
                  minute: "2-digit",
                })}
              </div>
              <button
                type="button"
                onClick={handleDone}
                style={{
                  border: "none",
                  background: "transparent",
                  color: "#2563eb",
                  fontSize: 14,
                  fontWeight: 600,
                }}
              >
                Done
              </button>
            </div>

            {/* signing canvas area */}
            <div
              style={{
                flex: 1,
                padding: "12px 16px 16px",
                background: "#e5e7eb",
                borderBottomLeftRadius: 20,
                borderBottomRightRadius: 20,
              }}
            >
              <div
                style={{
                  position: "relative",
                  width: "100%",
                  height: "100%",
                  borderRadius: 16,
                  background: "#f3f4f6",
                  padding: "8px 10px",
                }}
              >
                <canvas
                  ref={canvasRef}
                  style={{
                    width: "100%",
                    height: "100%",
                    borderRadius: 14,
                    touchAction: "none",
                  }}
                  onPointerDown={handlePointerDown}
                  onPointerMove={handlePointerMove}
                  onPointerUp={handlePointerUp}
                  onPointerLeave={handlePointerUp}
                />
                <div
                  style={{
                    position: "absolute",
                    left: 0,
                    right: 0,
                    bottom: 22,
                    textAlign: "center",
                    pointerEvents: "none",
                  }}
                >
                  <div
                    style={{
                      borderBottom: "1px dashed #9ca3af",
                      margin: "0 32px",
                    }}
                  />
                  <div
                    style={{
                      marginTop: 8,
                      fontSize: 13,
                      color: "#9ca3af",
                      fontWeight: 500,
                    }}
                  >
                    Sign above the dotted line
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function QuestionInput({ q, val, setVal, readOnly, projects = [], documentNumber = null, template }) {
  // Robust normalize: accept aliases and fallback to label (or text/title) hint
  const rawType = (q?.type || "").toString().toLowerCase().trim();
  const labelSource = q?.label ?? q?.text ?? q?.title ?? "";
  const labelHint = labelSource.toString().toLowerCase();

  // If author left it as text but the label implies location, treat it as location.
  const locationLabelRegex =
    /(location|site\s*location|job\s*site|address|coords?|coordinates|lat|lng|gps|map|pin|where)/;

  // NEW: scan the full question object for location hints
  const blob = JSON.stringify(q || {}).toLowerCase();
  const blobHasLocationHints =
    blob.includes('"location"') ||
    blob.includes('"gps"') ||
    blob.includes('"lat"') ||
    blob.includes('"lng"') ||
    blob.includes('"latitude"') ||
    blob.includes('"longitude"') ||
    blob.includes('"coordinate"') ||
    blob.includes('"address"') ||
    blob.includes('select on map');

  const t =
    rawType && rawType !== "short_text" && rawType !== "text"
      ? rawType
      : (locationLabelRegex.test(labelHint) ||
         labelHint.includes("location") ||    // <- permissive includes
         labelHint.includes("address")  ||    // <- permissive includes
         blobHasLocationHints)
      ? "location"
      : rawType;

  // TEMP DEBUG: check how this question is being resolved (remove after testing)
  if (process.env.NODE_ENV !== "production") {
    // eslint-disable-next-line no-console
    console.log("Question debug:", { id: q?.id, rawType: q?.type, resolvedType: t, label: q?.label });
  }

  switch (t) {
    case "short_text":
      return <input value={val || ""} onChange={e => setVal(e.target.value)} style={{ width: "85%", padding: 6 }} readOnly={readOnly} disabled={readOnly} />;
    case "paragraph":
      return <textarea value={val || ""} onChange={e => setVal(e.target.value)} style={{ width: "85%", minHeight: 50, padding: 6 }} readOnly={readOnly} disabled={readOnly} />;
    case "multiple_choice": {
      // Resolve order: custom options → responseSet in template.responseSets → fallback defaults
      const resolveFromTemplateSets = () => {
        const ref = q?.responseSet;
        if (!ref || !ref.type || !ref.id) return null;
        const pool = ref.type === "template"
          ? template?.responseSets?.templateSets
          : ref.type === "global"
          ? template?.responseSets?.globalSets
          : null;
        if (!Array.isArray(pool)) return null;
        const found = pool.find((s) => String(s.id) === String(ref.id));
        return Array.isArray(found?.options) && found.options.length ? found.options : null;
      };
      const opts =
        (Array.isArray(q.options) && q.options.length && q.options) ||
        resolveFromTemplateSets() ||
        ["Option 1", "Option 2"];
      return (
        <div>
          {opts.map((o, i) => (
            <label key={i} style={{ marginRight: 12 }}>
              <input
                type="radio"
                name={q.id}
                checked={val === o}
                onChange={() => setVal(o)}
                disabled={readOnly}
              />{" "}
              {o}
            </label>
          ))}
        </div>
      );
    }
    case "checkbox":
      return (
        <div style={{ display: "flex", alignItems: "center", minHeight: 28, gap: 8 }}>
          <input
            type="checkbox"
            checked={!!val}
            onChange={e => setVal(e.target.checked)}
            disabled={readOnly}
          />
          <span>{labelSource || "Checkbox"}</span>
        </div>
      );
    case "date":
      return (
        <input
          type="date"
          value={val || ""}
          onChange={e => setVal(e.target.value)}
          readOnly={readOnly}
          disabled={readOnly}
        />
      );
    case "signature":
      return (
        <SignatureAnswer
          value={val}
          onChange={setVal}
          readOnly={readOnly}
        />
      );
    case "media":
      return (
        <FormQuestionRenderer
          q={q}
          value={val}
          onChange={setVal}
          readOnly={readOnly}
        />
      );
    case "slider": {
      const min = typeof q.sliderMin === "number" ? q.sliderMin : 0;
      const max = typeof q.sliderMax === "number" ? q.sliderMax : 10;
      const step =
        typeof q.sliderStep === "number" && q.sliderStep > 0 ? q.sliderStep : 1;
      let current = typeof val === "number" ? val : min;
      if (current < min) current = min;
      if (current > max) current = max;

      return (
        <div style={{ width: "100%" }}>
          <input
            type="range"
            min={min}
            max={max}
            step={step}
            value={current}
            onChange={e => setVal(Number(e.target.value))}
            disabled={readOnly}
            style={{ width: "100%" }}
          />
          <div
            className="slider-preview-scale"
            style={{
              display: "flex",
              justifyContent: "space-between",
              marginTop: 4,
              fontSize: 11,
              color: "#6b7280",
            }}
          >
            <span>{min}</span>
            <span style={{ fontWeight: 600, color: "#111827" }}>
              {current}
            </span>
            <span>{max}</span>
          </div>
        </div>
      );
    }
    case "site":
      return (
        <select
          value={val || ""}
          onChange={e => setVal(e.target.value)}
          style={{ width: "85%", padding: 6 }}
          disabled={readOnly}
        >
          <option value="" disabled>Select a site…</option>
          {projects.map(p => (
            <option key={p.id} value={String(p.id)}>
              {p.name || p.title || `Project ${p.id}`}
            </option>
          ))}
        </select>
      );
    case "person": {
      const u = JSON.parse(localStorage.getItem("user") || "null");
      const currentUserName =
        u?.name || u?.displayName || (u?.email ? u.email.split("@")[0] : "");
      const display = (val ?? "") || currentUserName || "";
      return (
        <input
          value={display}
          onChange={() => {}}
          style={{ width: "85%", padding: 6 }}
          readOnly
          disabled
        />
      );
    }
    case "location":
    case "geo":
    case "geolocation":
    case "map":
    case "gps":
    case "address": {
      // Accept legacy "lat,lng" strings by converting to an object once.
      let value = val;
      if (typeof value === "string" && value.includes(",")) {
        const [latStr, lngStr] = value.split(",").map(s => s.trim());
        const lat = parseFloat(latStr), lng = parseFloat(lngStr);
        if (!Number.isNaN(lat) && !Number.isNaN(lng)) {
          value = { lat, lng, address: "", timestamp: Date.now() };
        }
      }
      return <LocationAnswer value={value} onChange={setVal} label={q.label ?? q.text ?? q.title} readOnly={readOnly} />;
    }
    case "annotation":
      return (
        <FormQuestionRenderer
          q={q}
          value={val}
          onChange={setVal}
          readOnly={readOnly}
        />
      );
    case "document-number": {
      const display = documentNumber ? `#${documentNumber}` : "";
      return (
        <input
          value={display}
          onChange={() => {}}
          placeholder={documentNumber ? undefined : "Assigned on save"}
          readOnly
          disabled
          style={{ width: "60%", padding: 6, fontWeight: 600 }}
        />
      );
    }
    case "instruction": {
      const styleKey = (q.instructionStyle || "info").toLowerCase();
      let fg = "#1976d2";
      let bg = "#e7f0fd";

      if (styleKey === "danger") {
        fg = "#b91c1c";
        bg = "#fee2e2";
      } else if (styleKey === "caution") {
        fg = "#92400e";
        bg = "#fef3c7";
      } else if (styleKey === "positive") {
        fg = "#166534";
        bg = "#dcfce7";
      }

      const url = q.instructionMediaUrl || "";
      const isPdf = typeof url === "string" && url.toLowerCase().includes(".pdf");

      return (
        <div
          style={{
            color: fg,
            background: bg,
            padding: 8,
            borderRadius: 8,
            fontSize: 14,
          }}
        >
          {q.label && (
            <div
              style={{
                fontStyle: "italic",
                marginBottom: q.instructionMediaUrl ? 6 : 0,
              }}
            >
              {q.label}
            </div>
          )}

          {q.instructionMediaUrl && (
            <div style={{ marginTop: 4 }}>
              {isPdf ? (
                <iframe
                  src={q.instructionMediaUrl}
                  title={q.instructionMediaName || "Instruction attachment"}
                  style={{
                    width: "100%",
                    height: 360,
                    border: "none",
                    borderRadius: 6,
                    background: "#fff",
                  }}
                />
              ) : (
                <img
                  src={q.instructionMediaUrl}
                  alt={q.instructionMediaName || "Instruction attachment"}
                  style={{
                    width: "100%",
                    maxHeight: 400,
                    borderRadius: 6,
                    objectFit: "contain",
                    display: "block",
                    background: "#fff",
                  }}
                />
              )}
            </div>
          )}
        </div>
      );
    }
    default:
      return <input value={val || ""} onChange={e => setVal(e.target.value)} readOnly={readOnly} disabled={readOnly} />;
  }
}

function LocationAnswer({ value, onChange, label = "Location", readOnly }) {
  const [open, setOpen] = useState(false);

  // Normalize possible "lat,lng" string to object (defensive)
  let cur = value || null;
  if (typeof cur === "string" && cur.includes(",")) {
    const [latStr, lngStr] = cur.split(",").map(s => s.trim());
    const lat = parseFloat(latStr), lng = parseFloat(lngStr);
    if (!Number.isNaN(lat) && !Number.isNaN(lng)) {
      cur = { lat, lng, address: "", timestamp: Date.now() };
    }
  }

  if (readOnly) {
    return cur ? (
      <div className="text-sm text-gray-700">
        <div>
          📍 {cur.address || (
            (Number.isFinite(cur?.lat) && Number.isFinite(cur?.lng))
              ? `${Number(cur.lat).toFixed(5)}, ${Number(cur.lng).toFixed(5)}`
              : ""
          )}
        </div>
        <div className="text-xs text-gray-500 mt-1">
          Saved: {cur?.timestamp ? new Date(cur.timestamp).toLocaleString() : "—"}
        </div>
      </div>
    ) : <div className="text-sm text-gray-500">No location selected</div>;
  }

  return (
    <div className="border rounded-xl p-3 flex items-center justify-between">
      {/* Left: current location info */}
      <div className="text-sm text-gray-700">
        {cur ? (
          <>
            <div>
              📍 {cur.address || (
                (Number.isFinite(cur?.lat) && Number.isFinite(cur?.lng))
                  ? `${Number(cur.lat).toFixed(5)}, ${Number(cur.lng).toFixed(5)}`
                  : ""
              )}
            </div>
            <div className="text-xs text-gray-500 mt-1">
              Saved: {cur?.timestamp ? new Date(cur.timestamp).toLocaleString() : "—"}
            </div>
          </>
        ) : (
          <div className="text-sm text-gray-500">No location selected</div>
        )}
      </div>

      {/* Right: button inline */}
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="ml-4 px-3 py-2 rounded-md bg-violet-600 text-white whitespace-nowrap"
      >
        Select on map
      </button>

      {/* Single modal */}
      {open && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl w-full max-w-3xl p-4">
            <h3 className="text-lg font-semibold mb-3">Select location</h3>
            <LocationField
              value={cur}
              onChange={(val) => {
                // ignore synthetic events that cause circular JSON
                if (val && (val.nativeEvent || val.target)) return;
                onChange(val);
                setOpen(false);
              }}
              onClose={() => setOpen(false)}
            />
          </div>
        </div>
      )}
    </div>
  );
}



